import { Entity } from '@backstage/catalog-model';
import {
  compatWrapper,
  convertLegacyRouteRef,
} from '@backstage/core-compat-api';
import { ApiBlueprint, configApiRef } from '@backstage/frontend-plugin-api';
import { scmAuthApiRef } from '@backstage/integration-react';
import { EntityContentBlueprint } from '@backstage/plugin-catalog-react/alpha';
import { PageShareTitle } from '../../lib/shareTitle';
import {
  GithubPullRequestsClient,
  githubPullRequestsApiRef,
  githubPullRequestsPlugin as legacyGithubPullRequestsPlugin,
} from '@roadiehq/backstage-plugin-github-pull-requests';

// The Roadie plugin only ships old-frontend-system extensions (no ./alpha
// export), so it is wrapped manually here. Tokens come from the GitHub OAuth
// popup flow via ScmAuth -> githubAuthApiRef.
const isGithubPullRequestsAvailable = (entity: Entity) =>
  entity.kind.toLocaleLowerCase('en-US') === 'component' &&
  Boolean(entity.metadata.annotations?.['github.com/project-slug']);

/** Registers the plugin's API so the wrapped legacy components can resolve it. */
export const githubPullRequestsApi = ApiBlueprint.make({
  params: defineParams =>
    defineParams({
      api: githubPullRequestsApiRef,
      deps: { configApi: configApiRef, scmAuthApi: scmAuthApiRef },
      factory: ({ configApi, scmAuthApi }) =>
        new GithubPullRequestsClient({ configApi, scmAuthApi }),
    }),
});

export const githubPullRequestsEntityContent = EntityContentBlueprint.make({
  name: 'pull-requests',
  params: {
    path: '/pull-requests',
    title: 'Pull Requests',
    filter: isGithubPullRequestsAvailable,
    routeRef: convertLegacyRouteRef(
      legacyGithubPullRequestsPlugin.routes.entityContent,
    ),
    loader: () =>
      import('@roadiehq/backstage-plugin-github-pull-requests').then(m =>
        compatWrapper(
          <PageShareTitle title="Pull requests">
            <m.EntityGithubPullRequestsContent />
          </PageShareTitle>,
        ),
      ),
  },
});

// NOTE: the plugin's EntityGithubPullRequestsOverviewCard (PR statistics) is
// intentionally NOT registered. Its usePullRequestsStatistics hook fetches
// the last 20 closed PRs and then issues 2 extra GitHub API calls per PR
// (getRepositoryData + pulls.listCommits) — ~41 uncached requests on every
// entity overview render, which made the overview page slow on busy repos.
// The Pull Requests tab (above) remains; its table fetches a single search
// page (default page size 5) per render and is not affected.
